import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { ManagementChildDtoIn } from '@/models/in/ManagementChildDtoIn';
import { useDeleteAuthApi } from '@/hooks/UseApiCalls';

interface DeleteChildDialogProps {
  open: boolean;
  onClose: () => void;
  child: ManagementChildDtoIn;
  onSuccess: () => void;
}

const DeleteChildDialog = ({
  open,
  onClose,
  child,
  onSuccess,
}: DeleteChildDialogProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const { mutate: deleteChild, isPending: isDeleting } = useDeleteAuthApi<void>(
    `management/children/${child.parentId}/${child.childId}`
  );

  const handleDelete = () => {
    setError(null);

    deleteChild(undefined, {
      onSuccess: () => {
        onSuccess();
        onClose();
      },
      onError: (err) => {
        console.error('Error deleting child:', err);
        setError(t('childrenManagement.deleteChildDialog.errorDeleting'));
      },
    });
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const message = error 
    ? `${t('childrenManagement.deleteChildDialog.irreversible')}\n\n${error}`
    : t('childrenManagement.deleteChildDialog.irreversible');

  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      title={t('childrenManagement.deleteChildDialog.title')}
      message={message}
      confirmText={isDeleting ? 'childrenManagement.deleteChildDialog.deleting' : 'childrenManagement.deleteChildDialog.confirmDelete'}
      cancelText="childrenManagement.deleteChildDialog.cancel"
      onConfirm={handleDelete}
      onCancel={handleClose}
      isLoading={isDeleting}
      variant="danger"
    />
  );
};

export default DeleteChildDialog;
